import React, { useMemo } from 'react';

import { Button, ButtonGroup } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { InfoCard } from '@eika/card';
import { Link } from '@eika/link';
import { Loader } from '@eika/loader';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../defaultConfig';
import { useBatchedEfakturaSigningResult } from '../../fetchHooks/useEfaktura';
import useTranslation, { format } from '../../hooks/useTranslation';
import { useSigningContext } from '../../signingContext/signingContext';

type Props = {
    retrySigning: () => void;
    close: () => void;
};

type BatchedSigningResultType = {
    numberOfFailures: number;
    status: 'error' | 'pending' | 'success';
};

const MultipleApprovalEFakturaSigningStatus: React.FC<Props> = ({ retrySigning, close }) => {
    const { frontPageLink, stoppedPaymentsLink } = useConfig();
    const {
        tryAgain,
        approveMultipleEFaktura: { signingResult: signResultText, toTheFrontpage, seeEFaktura },
    } = useTranslation();
    const { dynamicLinkId, signingError, signingOperationPending } = useSigningContext();
    const {
        data: batchedEfakturaSigningResult,
        status: queryStatus,
        error,
        refetch,
    } = useBatchedEfakturaSigningResult(dynamicLinkId);

    const { numberOfFailures, status } = useMemo((): BatchedSigningResultType => {
        if (!batchedEfakturaSigningResult || queryStatus !== 'success')
            return { numberOfFailures: 0, status: queryStatus };
        const totalNumber = batchedEfakturaSigningResult.length;
        const numberOfFailures = batchedEfakturaSigningResult.filter((result) => result.status === 'REJECTED').length;
        const numberOfSuccesses = batchedEfakturaSigningResult.filter((result) => result.status === 'COMPLETED').length;
        const signingStatus = numberOfFailures > 0 || numberOfSuccesses !== totalNumber ? 'error' : queryStatus;
        return {
            numberOfFailures,
            status: signingStatus,
        };
    }, [batchedEfakturaSigningResult, queryStatus]);

    if (signingError) {
        return (
            <ErrorCardWrapper
                className="multi-approval-efaktura__modal-status-wrapper"
                exception={signingError}
                isModal
            >
                <ButtonWithSpinner showSpinner={signingOperationPending} onClick={retrySigning}>
                    {tryAgain}
                </ButtonWithSpinner>
            </ErrorCardWrapper>
        );
    }

    if (status === 'pending') {
        return (
            <div className="multi-approval-efaktura__modal-status-wrapper">
                <Loader />
            </div>
        );
    }

    if (status === 'success') {
        return (
            <InfoCard
                className="multi-approval-efaktura__modal-status-wrapper"
                skipBackground
                img={<RichIcon type={RichIconTypes.FERDIG} />}
            >
                <InfoCard.TitleH3>{signResultText.success.allEFakturasWasApproved}</InfoCard.TitleH3>
                <p className="multi-approval-efaktura__modal-status-description">
                    {signResultText.success.eFakturasArePaidOnDueDate}
                </p>
                {frontPageLink?.url && (
                    <Link
                        className="multi-approval-efaktura__modal-success-button"
                        linkType="button-positive"
                        href={frontPageLink?.url}
                    >
                        {toTheFrontpage}
                    </Link>
                )}
            </InfoCard>
        );
    }

    if (status === 'error' && numberOfFailures > 0)
        return (
            <InfoCard
                className="multi-approval-efaktura__modal-status-wrapper"
                skipBackground
                img={<RichIcon type={RichIconTypes.ADVARSEL} />}
            >
                <InfoCard.TitleH3>
                    {format(signResultText.error.nEFakturaWasNotApproved, numberOfFailures)}
                </InfoCard.TitleH3>
                <p className="multi-approval-efaktura__modal-status-description">
                    {signResultText.error.looksLikeWeCouldNotApproveAllYourEFaktura}
                </p>
                {stoppedPaymentsLink && (
                    <ButtonGroup
                        className="multi-approval-efaktura__modal-status-button"
                        primaryAction={<Button onClick={close}>{seeEFaktura}</Button>}
                        tertiaryAction={frontPageLink && <Link href={frontPageLink?.url}>{toTheFrontpage}</Link>}
                    />
                )}
            </InfoCard>
        );

    return (
        <ErrorCardWrapper className="multi-approval-efaktura__modal-status-wrapper" exception={error} isModal>
            <ButtonWithSpinner showSpinner={queryStatus === 'pending'} onClick={() => refetch()}>
                {tryAgain}
            </ButtonWithSpinner>
        </ErrorCardWrapper>
    );
};

export default MultipleApprovalEFakturaSigningStatus;
